import SEO from '../components/SEO';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Zap, TrendingUp, Clock, Award, Target, Users, AlertCircle, ArrowRight, Check, Calendar, Phone, Mail, RefreshCw, X, Cloud, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackFormSubmission } from '../utils/analytics';
import axios from 'axios';

const PartnerTransferLanding = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', currentPartner: '', painPoints: '', phone: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `Current Partner: ${formData.currentPartner}\\nPain Points: ${formData.painPoints}`,
        source: 'Partner Transfer Landing Page'
      });
      trackFormSubmission('Partner Transfer Form');
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="SAP Business One Partner Transfer | Switch to Ascarii with Confidence"
        description="Unhappy with your current SAP partner? Transfer to Ascarii with zero disruption. Free health check, smooth transition, better support guaranteed."
        keywords="SAP partner transfer, change SAP partner, SAP B1 support transfer, switch ERP partner, new SAP partner"
        canonical="https://www.ascarii.com/landing/partner-transfer"
      />

      {/* Hero - Centered Style */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6">For SAP Business One Customers</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">SAP Business One Support That Puts You First</h1>
            <p className="text-2xl md:text-3xl text-sky-300 mb-8 font-medium">The Ascarii Experience Promise - Partnership Support Without the Overhead</p>
            
            <div className="inline-block px-6 py-3 bg-cyan-400/20 backdrop-blur-sm border-2 border-cyan-400/50 rounded-xl mb-8">
              <p className="text-2xl text-cyan-100 font-bold">
                Free Upgrades • Free Cloud Migration • Service Hours That Never Expire
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12 text-left">
              {[
                "Free 30-day test environment for every upgrade",
                "Zero professional services fees for upgrades",
                "Free AWS cloud hosting design & build service",
                "Service Hours from £123/hour that never expire",
                "Dedicated account manager who knows your business"
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a href="#assessment">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-5 text-xl font-semibold border-2 border-white">
                  <Calendar className="w-6 h-6 mr-2" />Book Transfer Assessment
                </Button>
              </a>
              <a href="#how-it-works">
                <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-5 text-xl font-semibold">
                  See How It Works
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm text-blue-100">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>SAP Premier Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>AWS Advanced Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-cyan-300" /><span>15+ Years Experience</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Changes When You Transfer</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl border border-gray-200 shadow-lg">
              <thead className="bg-gradient-to-r from-blue-600 to-sky-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Service</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Previous Partner</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold bg-blue-700">With Ascarii</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { service: 'SAP B1 Upgrade to Latest', typical: '£8k-£15k', ascarii: 'FREE', highlight: true },
                  { service: '30-Day Test Environment', typical: 'Not offered', ascarii: 'FREE', highlight: true },
                  { service: 'Cloud Hosting Design & Build', typical: '£5k-£10k', ascarii: 'FREE', highlight: true },
                  { service: 'Professional Services Rate', typical: '£150-£200/h', ascarii: '£123-£150/h' },
                  { service: 'Service Hours Expiry', typical: 'Often expire', ascarii: 'Never expire', highlight: true },
                  { service: 'Response Time', typical: 'Days', ascarii: '<2 hours' },
                  { service: 'Dedicated Account Manager', typical: 'Rare', ascarii: 'Standard', highlight: true },
                  { service: 'AWS Uptime SLA', typical: 'Variable', ascarii: '99.995%' }
                ].map((row, i) => (
                  <tr key={i} className={row.highlight ? 'bg-green-50' : ''}>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.service}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">{row.typical}</td>
                    <td className={`px-6 py-4 text-center text-sm font-bold ${row.highlight ? 'text-green-700 bg-green-50' : 'text-blue-700'}`}>{row.ascarii}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Signs You've Outgrown */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Signs You've Outgrown Your Current Partner</h2>
            <p className="text-xl text-gray-600">Sound familiar?</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Slow Response Times", desc: "Waiting days for responses. Issues sit in queues. Feeling like you're not a priority." },
              { title: "Surprise Costs", desc: "Every request comes with a quote. No transparency. Budgets blown by unexpected fees." },
              { title: "No Proactive Support", desc: "Reactive only. No strategic guidance. No one thinks about what you need next." },
              { title: "Upgrade Anxiety", desc: "Quoted £8k-£15k for upgrades. Worried about disruption. Stuck on old versions." },
              { title: "Just Another Ticket", desc: "No dedicated contact. Different person each time. They don't know your business." },
              { title: "Cloud Migration Blocked", desc: "Massive quotes for cloud projects. No clear path. Stuck on-premise." }
            ].map((sign, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-white rounded-xl border-l-4 border-orange-600 shadow-md">
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{sign.title}</h3>
                  <p className="text-gray-600 text-sm">{sign.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Partnership Should Feel Different</h3>
            <p className="text-xl text-blue-100">Ascarii removes the overhead, surprises, and anxiety. See what genuine SAP partnership looks like.</p>
          </div>
        </div>
      </section>

      {/* Transfer Stories */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Real Partner Transfer Stories</h2>
          </div>
          <div className="space-y-8">
            {[
              { quote: "Our previous partner quoted £12,000 for an upgrade. Ascarii did it with zero fees, gave us a 30-day test environment to validate everything, and we went live in 6 weeks. Now we're on AWS cloud with Service Hour Bundles - complete budget control.", company: "Manufacturing SME", result: "Saved £12,000 on upgrade + £1,800/year on support" },
              { quote: "Response times went from 2-3 days to same-day. We have a dedicated account manager who actually knows our business. The free cloud migration got us off our ageing server with zero project fees. Game changer.", company: "Distribution Company", result: "Eliminated on-premise infrastructure + 99.995% uptime" }
            ].map((story, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">"{story.quote}"</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">- {story.company}</p>
                  <p className="text-sm font-semibold text-green-700">{story.result}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Transfer - Benefits Pillars */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Customers Transfer to Ascarii</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Three pillars of value from genuine partnership</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: RefreshCw, title: "Free Upgrades & Testing", strapline: "No professional services fees", desc: "Zero charges for upgrading to latest SAP Business One. Free 30-day test environment for every upgrade. This is standard with Ascarii, not a promotion." },
              { icon: Cloud, title: "Complete Cloud Solution", strapline: "Free design & build", desc: "AWS cloud hosting with free design and build service. 99.995% uptime, 24×7 support, UK/EU data sovereignty. Then predictable monthly costs." },
              { icon: BarChart3, title: "Predictable Costs", strapline: "Service Hours that never expire", desc: "Lock in rates from £123/hour (save up to 18%). Spread costs over 12-36 months. Hours are yours to keep whilst you're our customer." }
            ].map((pillar, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <pillar.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                    <p className="text-sm text-cyan-300 font-medium mb-2">{pillar.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ascarii Experience Promise */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-bold mb-4">The Ascarii Experience Promise</div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What You Get as an Ascarii Customer</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-green-200">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Free 30-Day Test Environments</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Every upgrade includes a dedicated 30-day test environment. Validate processes, train users, test customisations - all before go-live. No risk, no pressure, no extra cost.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-sky-500 flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Zero Upgrade Service Fees</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We don't charge professional services fees for upgrading existing customers to the latest SAP Business One. Save £8,000-£15,000 that other partners would charge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-8 md:p-10 text-white text-center shadow-2xl">
            <RefreshCw className="w-16 h-16 mx-auto mb-6 text-cyan-300" />
            <h3 className="text-3xl font-bold mb-4">Free Cloud Hosting Design & Build</h3>
            <p className="text-xl text-blue-100 mb-6">
              Moving to AWS cloud? We design and build your cloud environment at no extra cost. Then enjoy predictable monthly hosting with 99.995% uptime and 24×7 support.
            </p>
            <a href="/cloud-hosting" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-blue-600 hover:bg-blue-50">Learn About Cloud Hosting</Button>
            </a>
          </div>
        </div>
      </section>

      {/* How Transfer Works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="how-it-works">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How the Transfer Works</h2>
            <p className="text-xl text-gray-600">Five clear steps with minimal disruption</p>
          </div>
          <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-2">
            {[
              { step: "1", title: "Discovery Call", desc: "We review your current setup, pain points, and what you need from a partner." },
              { step: "2", title: "Transition Plan", desc: "Clear timeline, handover process, and service agreement. No lock-in contracts." },
              { step: "3", title: "Knowledge Transfer", desc: "Your current partner hands over documentation. We get up to speed on your system." },
              { step: "4", title: "Seamless Takeover", desc: "Support transitions to Ascarii. Your business continues without interruption." },
              { step: "5", title: "Partnership Begins", desc: "Dedicated account manager assigned. Free upgrade assessment. Service Hours if needed." }
            ].map((step, i) => (
              <React.Fragment key={i}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all text-center w-full md:flex-1 md:max-w-[200px] flex flex-col">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xl mb-4 mx-auto">{step.step}</div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">{step.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed flex-1">{step.desc}</p>
                </motion.div>
                {i < 4 && (
                  <ArrowRight className="hidden md:block w-6 h-6 text-blue-600 flex-shrink-0 self-center" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Service Hours */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Predictable Costs with Service Hour Bundles</h2>
              <p className="text-lg text-gray-600 mb-6">
                Stop paying £150/hour PAYG. Lock in rates from £123/hour and spread costs over 12-36 months. Your hours never expire whilst you're an Ascarii customer.
              </p>
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-bold text-gray-900 mb-1">Save £1,800 on 100 hours</p>
                  <p className="text-sm text-gray-600">Bundle cost £13,200 vs PAYG £15,000 over 24 months</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="font-bold text-gray-900 mb-1">Save £5,400 on 200 hours</p>
                  <p className="text-sm text-gray-600">Bundle cost £24,600 vs PAYG £30,000 over 24 months</p>
                </div>
              </div>
              <a href="/landing/ascarii-service-hours#calculator" target="_blank" rel="noopener noreferrer">
                <Button className="bg-blue-600 hover:bg-blue-700">Calculate Your Savings</Button>
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=85" alt="Service Hours" className="rounded-2xl shadow-2xl" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Partner Transfer Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "Are we locked into a contract with our current partner?", a: "Most SAP partners use annual rolling contracts or flexible terms. Review your agreement - you likely have a 30 or 90-day notice period. We'll help you understand your obligations and plan the transition accordingly." },
              { q: "Will this disrupt our business?", a: "No. The transfer happens in the background. Your SAP system continues running. Support transitions seamlessly from your old partner to Ascarii. Most customers experience zero disruption to daily operations." },
              { q: "What about our customisations and add-ons?", a: "We take over support for all your customisations and add-ons. During knowledge transfer, we document everything. If you have Boyum, Produmex, Beas, or other certified add-ons, we're experts in those too." },
              { q: "How long does a partner transfer take?", a: "Typically 2-4 weeks from decision to full transition. This includes discovery, knowledge transfer from your current partner, and Ascarii taking over support. We can accelerate if you have urgent needs." },
              { q: "What if we're mid-project with our current partner?", a: "We can wait until your current project completes, or take it over if there are issues. We'll review the project status and give you options. No pressure to abandon work in progress." },
              { q: "What about our data and IP?", a: "Your data and intellectual property remain yours. During transfer, we sign NDAs as standard. Nothing changes ownership - we're just taking over the support and partnership role." },
              { q: "What's the cost?", a: "The transfer itself has no fees. You then pay for: SAP maintenance (which you're already paying), cloud hosting if you want it, and support hours as needed. We'll show you exact costs upfront - no surprises." }
            ].map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment Form */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100" id="assessment">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Book Your Transfer Assessment</h2>
              <p className="text-xl text-gray-600 mb-8">Confidential review of your current setup and clear transition roadmap</p>
              
              <div className="space-y-6">
                {[
                  { icon: Shield, title: "Confidential Discussion", desc: "Everything discussed remains private and confidential" },
                  { icon: Target, title: "No Obligation", desc: "Free assessment with no pressure to commit" },
                  { icon: Clock, title: "Quick Process", desc: "Most transfers complete in 2-4 weeks" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              {submitted ? (
                <div className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
                  <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-green-600" />
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
                  <p className="text-gray-600">We'll be in touch within one business day for a confidential discussion about your partner transfer.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Started</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                      <input type="text" required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Current SAP Partner (Optional)</label>
                      <input type="text" value={formData.currentPartner} onChange={(e) => setFormData({...formData, currentPartner: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Main Pain Points (Optional)</label>
                      <textarea value={formData.painPoints} onChange={(e) => setFormData({...formData, painPoints: e.target.value})} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0" placeholder="e.g., Slow support, high costs, poor communication..."></textarea>
                    </div>
                    <div>
                      <label className="flex items-start gap-3">
                        <input type="checkbox" required checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} className="mt-1 w-5 h-5 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">I agree to Ascarii contacting me confidentially and have read the <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. *</span>
                      </label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                      {loading ? 'Sending...' : 'Request Confidential Assessment'}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">Completely confidential • No obligation</p>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Experience True Partnership?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join SAP Business One customers who transferred to Ascarii for genuine partnership support, transparent pricing, and the Ascarii Experience Promise.
            </p>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-12 border border-white/20">
              <p className="text-xl text-white font-semibold mb-4">When you transfer to Ascarii, you get:</p>
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-3xl mx-auto">
                {[
                  "Free SAP B1 upgrade (save £8k-£15k)",
                  "Free 30-day test environment",
                  "Free cloud hosting design & build",
                  "Service Hours that never expire",
                  "Dedicated account manager",
                  "No surprise costs or hidden fees"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              <a href="#assessment" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-10 py-5 text-xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Book Assessment
              </a>
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Talk to an Expert
              </a>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">SAP Premier Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">AWS Advanced Partner</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-6 h-6 text-cyan-300" /><span className="text-lg">15+ Years Experience</span></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerTransferLanding;
